import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Phone, Mail, Cake, CalendarCheck, IndianRupee, Camera, Plus, Award, CheckCircle2, FileDown, Images, ChevronLeft, ChevronRight, Send } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";

const GRADE_LEVELS = ["Emerging", "Developing", "Secure", "Exceeding"];
const CATEGORIES = ["Language", "Motor", "Social", "Cognitive", "Self-Care"];
const TERMS = ["Term 1", "Term 2", "Term 3"];

export default function ChildProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [child, setChild] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [fees, setFees] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [grades, setGrades] = useState([]);
  const [msDialog, setMsDialog] = useState(false);
  const [gradeDialog, setGradeDialog] = useState(false);
  const [msForm, setMsForm] = useState({ label: "", category: "Cognitive", achieved: false, notes: "" });
  const [gForm, setGForm] = useState({ term: "Term 1", academic_year: "2025-26",
    language: "Developing", motor: "Developing", social: "Developing", cognitive: "Developing", self_care: "Developing", remarks: "" });
  const [albumMonth, setAlbumMonth] = useState(new Date().toISOString().slice(0, 7));
  const [album, setAlbum] = useState(null);

  const canEdit = user?.role === "admin" || user?.role === "teacher";

  const load = async () => {
    const [c, a, f, u, m, g] = await Promise.all([
      api.get(`/children/${id}`),
      api.get(`/attendance/child/${id}`),
      api.get("/fees", { params: {} }),
      api.get("/daily-updates", { params: { child_id: id } }),
      api.get(`/milestones/${id}`),
      api.get(`/term-grades/${id}`),
    ]);
    setChild(c.data);
    setAttendance(a.data);
    setFees(f.data.filter((x) => x.child_id === id));
    setUpdates(u.data);
    setMilestones(m.data);
    setGrades(g.data);
    if (g.data.length) setGForm({ ...gForm, ...g.data[0] });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const loadAlbum = async (m) => {
    setAlbum(null);
    try {
      const { data } = await api.get(`/children/${id}/photo-album`, { params: { month: m } });
      setAlbum(data);
    } catch {
      setAlbum({ updates: [], total_photos: 0, month: m });
    }
  };

  useEffect(() => { loadAlbum(albumMonth); /* eslint-disable-next-line */ }, [id, albumMonth]);

  const shiftMonth = (delta) => {
    const [y, mo] = albumMonth.split("-").map(Number);
    const d = new Date(y, mo - 1 + delta, 1);
    setAlbumMonth(d.toISOString().slice(0, 7));
  };
  const albumMonthLabel = new Date(albumMonth + "-01").toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const saveMilestone = async () => {
    await api.post("/milestones", { ...msForm, child_id: id });
    toast.success("Milestone added");
    setMsDialog(false);
    setMsForm({ label: "", category: "Cognitive", achieved: false, notes: "" });
    load();
  };

  const toggleMilestone = async (m) => {
    await api.put(`/milestones/${m.id}`, { ...m, achieved: !m.achieved });
    load();
  };

  const saveGrade = async () => {
    await api.post("/term-grades", { ...gForm, child_id: id });
    toast.success("Report saved");
    setGradeDialog(false);
    load();
  };

  if (!child) return <div className="py-10 text-muted-foreground">Loading...</div>;

  const attStats = (() => {
    if (!attendance.length) return { pct: 0, present: 0, total: 0 };
    const present = attendance.filter((a) => a.status === "Present").length;
    return { pct: Math.round((present / attendance.length) * 100), present, total: attendance.length };
  })();

  return (
    <div className="space-y-6">
      <Link to="/children" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Back to Children
      </Link>

      {/* Header */}
      <div className="card-soft rounded-3xl p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-6">
          <div className="w-20 h-20 rounded-3xl chip-green flex items-center justify-center text-3xl font-black">
            {child.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-black">{child.name}</h1>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="chip-green px-3 py-1 rounded-full text-xs font-bold">{child.class_name}</span>
              <span className="chip-teal px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                <Cake size={12} /> {child.date_of_birth}
              </span>
              <span className="chip-amber px-3 py-1 rounded-full text-xs font-bold">
                Admitted {child.admission_date}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><div className="text-xs uppercase font-bold text-muted-foreground">Parent</div><div className="font-bold">{child.parent_name}</div></div>
          <div><div className="text-xs uppercase font-bold text-muted-foreground">Phone</div><div className="font-bold flex items-center gap-1"><Phone size={13} /> {child.parent_phone}</div></div>
          <div><div className="text-xs uppercase font-bold text-muted-foreground">Email</div><div className="font-bold truncate flex items-center gap-1"><Mail size={13} /> {child.parent_email || "—"}</div></div>
          <div><div className="text-xs uppercase font-bold text-muted-foreground">Occupation</div><div className="font-bold">{child.parent_occupation || "—"}</div></div>
        </div>
        {child.notes && <div className="mt-4 p-4 rounded-2xl bg-muted italic text-sm">{child.notes}</div>}
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-full h-12 bg-muted p-1 overflow-x-auto flex flex-wrap md:flex-nowrap">
          <TabsTrigger value="overview" className="rounded-full data-[state=active]:bg-white font-bold">Overview</TabsTrigger>
          <TabsTrigger value="progress" className="rounded-full data-[state=active]:bg-white font-bold">Academic Progress</TabsTrigger>
          <TabsTrigger value="attendance" className="rounded-full data-[state=active]:bg-white font-bold">Attendance</TabsTrigger>
          <TabsTrigger value="fees" className="rounded-full data-[state=active]:bg-white font-bold">Fees</TabsTrigger>
          <TabsTrigger value="updates" className="rounded-full data-[state=active]:bg-white font-bold">Daily Updates</TabsTrigger>
          <TabsTrigger value="album" className="rounded-full data-[state=active]:bg-white font-bold">Album</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card-soft rounded-3xl p-6">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Attendance</div>
              <div className="text-4xl font-black mt-1">{attStats.pct}%</div>
              <div className="text-sm text-muted-foreground mt-1">{attStats.present}/{attStats.total} days</div>
            </div>
            <div className="card-soft rounded-3xl p-6">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Pending Fees</div>
              <div className="text-4xl font-black mt-1">
                ₹{fees.filter((f) => !f.paid).reduce((s, f) => s + f.amount, 0).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground mt-1">{fees.filter((f) => !f.paid).length} items due</div>
            </div>
            <div className="card-soft rounded-3xl p-6">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Milestones</div>
              <div className="text-4xl font-black mt-1">
                {milestones.filter((m) => m.achieved).length}
                <span className="text-lg text-muted-foreground">/{milestones.length}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">achieved</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="progress" className="mt-6 space-y-6">
          <Card className="rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><CheckCircle2 className="text-primary" size={20} /> Developmental Milestones</CardTitle>
              {canEdit && <Button data-testid="add-milestone" onClick={() => setMsDialog(true)} className="rounded-full" size="sm"><Plus size={14} /> Milestone</Button>}
            </CardHeader>
            <CardContent>
              {CATEGORIES.map((cat) => {
                const list = milestones.filter((m) => m.category === cat);
                if (!list.length) return null;
                return (
                  <div key={cat} className="mb-4">
                    <div className="text-xs uppercase font-bold text-muted-foreground tracking-widest mb-2">{cat}</div>
                    <div className="space-y-2">
                      {list.map((m) => (
                        <div key={m.id} data-testid={`ms-${m.id}`} className="flex items-center gap-3 p-3 rounded-2xl bg-muted/50 hover:bg-muted">
                          <button
                            disabled={!canEdit}
                            onClick={() => toggleMilestone(m)}
                            className={`w-8 h-8 rounded-xl flex items-center justify-center ${m.achieved ? "bg-primary text-primary-foreground" : "bg-white border-2 border-border"}`}
                          >
                            {m.achieved && <CheckCircle2 size={16} />}
                          </button>
                          <div className="flex-1">
                            <div className={`font-bold ${m.achieved ? "text-foreground" : "text-muted-foreground"}`}>{m.label}</div>
                            {m.achieved && m.date_achieved && <div className="text-xs text-muted-foreground">Achieved {m.date_achieved}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {milestones.length === 0 && (
                <div className="text-muted-foreground text-center py-8">No milestones yet.</div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><Award className="text-primary" size={20} /> Term Report Cards</CardTitle>
              {canEdit && <Button data-testid="add-grade" onClick={() => setGradeDialog(true)} className="rounded-full" size="sm"><Plus size={14} /> Add / Update Term</Button>}
            </CardHeader>
            <CardContent className="space-y-3">
              {grades.length === 0 && <div className="text-muted-foreground text-center py-8">No term reports yet.</div>}
              {grades.map((g) => (
                <div key={g.id} className="rounded-3xl border border-border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-extrabold text-lg">{g.term} · {g.academic_year}</div>
                    </div>
                    <Button
                      data-testid={`pdf-${g.id}`}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-primary/40 text-primary hover:bg-primary/10 font-bold"
                      onClick={async () => {
                        try {
                          const res = await api.get(`/term-grades/${g.id}/pdf`, { responseType: "blob" });
                          const blob = new Blob([res.data], { type: "application/pdf" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${child.name.replace(/ /g, "_")}_${g.term.replace(/ /g, "")}_${g.academic_year}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          setTimeout(() => URL.revokeObjectURL(url), 1000);
                          toast.success("Report card downloaded");
                        } catch {
                          toast.error("Could not download");
                        }
                      }}
                    >
                      <FileDown size={14} /> Download PDF
                    </Button>
                    {child.parent_email && (
                      <Button
                        data-testid={`email-report-${g.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-full border-primary/40 text-primary hover:bg-primary/10 font-bold ml-2"
                        onClick={async () => {
                          try {
                            await api.post(`/emails/report-card/${g.id}`);
                            toast.success(`Report card emailed to ${child.parent_email}`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Email failed");
                          }
                        }}
                      >
                        <Send size={14} /> Email to Parent
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    {[["Language", g.language], ["Motor", g.motor], ["Social", g.social], ["Cognitive", g.cognitive], ["Self-Care", g.self_care]].map(([k, v]) => (
                      <div key={k} className="rounded-2xl bg-muted p-3">
                        <div className="text-xs text-muted-foreground font-semibold uppercase">{k}</div>
                        <div className="font-bold">{v || "—"}</div>
                      </div>
                    ))}
                  </div>
                  {g.remarks && <div className="mt-3 italic text-sm text-muted-foreground">"{g.remarks}"</div>}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attendance" className="mt-6">
          <Card className="rounded-3xl">
            <CardHeader><CardTitle className="flex items-center gap-2"><CalendarCheck className="text-primary" size={20} /> Recent Attendance</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {attendance.slice(0, 30).map((a) => (
                  <div key={a.date} className={`p-3 rounded-xl ${a.status === "Present" ? "chip-green" : "chip-pink"}`}>
                    <div className="text-xs font-bold uppercase">{a.date}</div>
                    <div className="font-black">{a.status}</div>
                  </div>
                ))}
                {attendance.length === 0 && <div className="col-span-full text-muted-foreground py-6">No attendance recorded.</div>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fees" className="mt-6">
          <Card className="rounded-3xl">
            <CardHeader><CardTitle className="flex items-center gap-2"><IndianRupee className="text-primary" size={20} /> Fee History</CardTitle></CardHeader>
            <CardContent>
              {fees.length === 0 && <div className="text-muted-foreground py-6 text-center">No fee records.</div>}
              <div className="space-y-2">
                {fees.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-4 rounded-2xl bg-muted/40">
                    <div>
                      <div className="font-bold">{f.title}</div>
                      <div className="text-xs text-muted-foreground">Due {f.due_date}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-black text-lg">₹{f.amount.toLocaleString()}</div>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${f.paid ? "chip-green" : "chip-pink"}`}>
                        {f.paid ? `Paid · ${f.payment_mode}` : "Unpaid"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="updates" className="mt-6">
          <Card className="rounded-3xl">
            <CardHeader><CardTitle className="flex items-center gap-2"><Camera className="text-primary" size={20} /> Daily Updates</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {updates.length === 0 && <div className="text-muted-foreground py-6 text-center">No updates yet.</div>}
              {updates.map((u) => (
                <div key={u.id} className="rounded-3xl border border-border p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold">{u.date}</div>
                    <div className="text-xs text-muted-foreground">by {u.posted_by}</div>
                  </div>
                  <p className="text-sm mb-2">{u.activity_note}</p>
                  {u.meal_note && <p className="text-sm text-muted-foreground">🍎 {u.meal_note}</p>}
                  {u.photos?.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {u.photos.map((p) => (
                        <AuthImage key={p} path={p} className="w-full h-24 object-cover rounded-xl" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="updates" className="mt-6">
          <Card className="rounded-3xl">
            <CardHeader><CardTitle className="flex items-center gap-2"><Camera className="text-primary" size={20} /> Daily Updates</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {updates.length === 0 && <div className="text-muted-foreground py-6 text-center">No updates yet.</div>}
              {updates.map((u) => (
                <div key={u.id} className="rounded-3xl border border-border p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold">{u.date}</div>
                    <div className="text-xs text-muted-foreground">by {u.posted_by}</div>
                  </div>
                  <p className="text-sm mb-2">{u.activity_note}</p>
                  {u.meal_note && <p className="text-sm text-muted-foreground">🍎 {u.meal_note}</p>}
                  {u.photos?.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {u.photos.map((p) => (
                        <AuthImage key={p} path={p} className="w-full h-24 object-cover rounded-xl" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="album" className="mt-6">
          <Card className="rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
              <CardTitle className="flex items-center gap-2">
                <Images className="text-primary" size={20} /> {albumMonthLabel} in photos
              </CardTitle>
              <div className="flex items-center gap-2">
                <button
                  data-testid="album-prev"
                  onClick={() => shiftMonth(-1)}
                  className="w-10 h-10 rounded-full bg-muted hover:bg-primary/10 flex items-center justify-center"
                  title="Previous month"
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="px-4 h-10 rounded-full bg-muted flex items-center font-bold text-sm min-w-[140px] justify-center">
                  {albumMonthLabel}
                </div>
                <button
                  data-testid="album-next"
                  onClick={() => shiftMonth(1)}
                  className="w-10 h-10 rounded-full bg-muted hover:bg-primary/10 flex items-center justify-center"
                  title="Next month"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {album === null && <div className="py-10 text-center text-muted-foreground">Loading album...</div>}
              {album && album.total_photos === 0 && (
                <div className="py-12 text-center">
                  <div className="text-5xl mb-3">📸</div>
                  <div className="font-bold">No photos yet this month</div>
                  <div className="text-sm text-muted-foreground mt-1">Daily updates with pictures will appear here.</div>
                </div>
              )}
              {album && album.total_photos > 0 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="chip-teal px-4 py-2 rounded-full text-sm font-bold w-fit">
                      {album.total_photos} {album.total_photos === 1 ? "photo" : "photos"} · {album.updates.length} {album.updates.length === 1 ? "day" : "days"}
                    </div>
                    {child.parent_email && (
                      <Button
                        data-testid="email-photo-recap"
                        size="sm"
                        variant="outline"
                        className="rounded-full border-primary/40 text-primary hover:bg-primary/10 font-bold"
                        onClick={async () => {
                          try {
                            await api.post(`/emails/photo-recap/${id}?month=${albumMonth}`);
                            toast.success(`Photo recap sent to ${child.parent_email}`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Email failed");
                          }
                        }}
                      >
                        <Send size={14} /> Email this month to parent
                      </Button>
                    )}
                  </div>
                  {album.updates.map((u) => (
                    <div key={u.id} className="rounded-3xl bg-muted/30 p-5 animate-float-in">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="chip-green px-3 py-1 rounded-full text-xs font-bold">
                          {new Date(u.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                        </span>
                        <span className="text-xs text-muted-foreground">by {u.posted_by}</span>
                      </div>
                      {u.activity_note && (
                        <p className="text-sm text-muted-foreground italic mb-3">"{u.activity_note}"</p>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {u.photos.map((p, i) => (
                          <AuthImage
                            key={p}
                            path={p}
                            className={`w-full object-cover rounded-2xl ${i % 5 === 0 ? "aspect-[4/5]" : "aspect-square"}`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Milestone dialog */}
      <Dialog open={msDialog} onOpenChange={setMsDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>Add Milestone</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Milestone</Label><Input data-testid="ms-label" value={msForm.label} onChange={(e) => setMsForm({ ...msForm, label: e.target.value })} className="h-11 rounded-xl" /></div>
            <div>
              <Label>Category</Label>
              <Select value={msForm.category} onValueChange={(v) => setMsForm({ ...msForm, category: v })}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={msForm.achieved} onCheckedChange={(v) => setMsForm({ ...msForm, achieved: !!v })} />
              <Label>Already achieved</Label>
            </div>
            <div><Label>Notes</Label><Textarea value={msForm.notes} onChange={(e) => setMsForm({ ...msForm, notes: e.target.value })} className="rounded-xl" /></div>
          </div>
          <DialogFooter><Button data-testid="save-ms" onClick={saveMilestone} className="rounded-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grade dialog */}
      <Dialog open={gradeDialog} onOpenChange={setGradeDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>Term Report</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Term</Label>
              <Select value={gForm.term} onValueChange={(v) => setGForm({ ...gForm, term: v })}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Academic Year</Label><Input value={gForm.academic_year} onChange={(e) => setGForm({ ...gForm, academic_year: e.target.value })} className="h-11 rounded-xl" /></div>
            {[["language", "Language"], ["motor", "Motor"], ["social", "Social"], ["cognitive", "Cognitive"], ["self_care", "Self-Care"]].map(([k, l]) => (
              <div key={k}>
                <Label>{l}</Label>
                <Select value={gForm[k] || "Developing"} onValueChange={(v) => setGForm({ ...gForm, [k]: v })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{GRADE_LEVELS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
            <div className="col-span-2"><Label>Remarks</Label><Textarea value={gForm.remarks} onChange={(e) => setGForm({ ...gForm, remarks: e.target.value })} className="rounded-xl" /></div>
          </div>
          <DialogFooter><Button data-testid="save-grade" onClick={saveGrade} className="rounded-full">Save Report</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
